import {
  find,
  includes,
  invoke,
  isEmpty,
  isNil,
  isString,
  keyBy,
  lastIndexOf,
  map,
  mapValues,
  maxBy,
  throttle,
} from 'lodash';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { PureComponent, createRef } from 'react';
import { Events, scroller } from 'react-scroll';
import styled, { css } from 'styled-components';

import { GroupDetailsButton } from '@components/group-details-button';
import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { analytics } from '../../analytics';
import { breakpoints } from '../../constants';
import { createNavIds } from '../../utils/createNavIds';
import { DietaryFilter } from '../dietary-filter';
import { Box, Flex } from '../grid';
import { AddedToOrderToast } from './added-to-order-toast';
import { OrderSettingsSummary } from '../order-settings-summary';
import { CartButton } from './cart-button';
import { Nav } from './nav';
import { Icon } from '../icons';

import { centerElementInScrollContainer, getElementVisibleHeight, giveSectionFocus } from './util';

export const LG_PLUS_BREAKPOINT = '1120px';
export const XL_PLUS_BREAKPOINT = '1250px';

export const NAV_ITEMS_COUNT = {
  LG: 3,
  LGPlus: 3,
  XLPlus: 4,
};

class InPageNav extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isScrolling: false,
      scrollListener: null,
      isMouseDown: false,
      startX: null,
      scrollLeft: null,
      mouseMoveListener: null,
      selectedSectionId: null,
      isItemClick: false,
      isSticky: false,
    };

    makeObservable(this, {
      navItems: computed,
    });

    this.inPageNavRef = createRef();
    this.affixRef = createRef();
    this.itemsWrapperRef = createRef();
    this.navMouseDown = this.navMouseDown.bind(this);
    this.navMouseUp = this.navMouseUp.bind(this);
    this.handleNavScroll = this.handleNavScroll.bind(this);
    this.handleScrollBegin = this.handleScrollBegin.bind(this);
    this.handleScrollEnd = this.handleScrollEnd.bind(this);
    this.handlePageScroll = this.handlePageScroll.bind(this);
    this.scrollToSection = this.scrollToSection.bind(this);
    this.selectItem = this.selectItem.bind(this);
    this.itemRefs = mapValues(
      keyBy(
        this.navItems.filter(({ id }) => isString(id) && !isEmpty(id)),
        'id'
      ),
      createRef
    );
  }

  componentDidMount() {
    this.itemsWrapperRef.current.addEventListener('mousedown', this.navMouseDown, false);
    this.itemsWrapperRef.current.addEventListener('mouseup', this.navMouseUp, false);

    const mouseMoveListener = throttle(this.handleNavScroll, 20);
    invoke(this.itemsWrapperRef.current, 'addEventListener', 'mousemove', mouseMoveListener);

    const scrollListener = throttle(this.handlePageScroll, 100);
    invoke(window, 'addEventListener', 'scroll', scrollListener);

    this.setState({
      mouseMoveListener,
      scrollListener,
    });

    Events.scrollEvent.register('begin', this.handleScrollBegin);
    Events.scrollEvent.register('end', this.handleScrollEnd);
    this.performInitialCategoryScroll();
  }

  componentDidUpdate(prevProps) {
    const { router } = this.props;
    if (prevProps.router !== router) {
      this.performInitialCategoryScroll();
    }
  }

  componentWillUnmount() {
    const { scrollListener, mouseMoveListener } = this.state;

    invoke(window, 'removeEventListener', 'scroll', scrollListener);
    invoke(this.itemsWrapperRef.current, 'removeEventListener', 'mousemove', mouseMoveListener);

    this.itemsWrapperRef.current.removeEventListener('mousedown', this.navMouseDown, false);
    this.itemsWrapperRef.current.removeEventListener('mouseup', this.navMouseUp, false);

    Events.scrollEvent.remove('begin');
    Events.scrollEvent.remove('end');
  }

  handleNavScroll(e) {
    const { isMouseDown, startX, scrollLeft } = this.state;

    if (!isMouseDown) return;

    e.preventDefault();

    const x = e.pageX - this.itemsWrapperRef.current.offsetLeft;
    const walk = x - startX;
    this.itemsWrapperRef.current.scrollLeft = scrollLeft - walk;
  }

  handleScrollBegin() {
    this.setState({
      isScrolling: true,
    });
  }

  handleScrollEnd() {
    this.setState({
      isScrolling: false,
    });

    const { selectedSectionId } = this.state;
    giveSectionFocus(selectedSectionId);
  }

  handlePageScroll() {
    const { isScrolling, selectedSectionId, isItemClick, isSticky } = this.state;

    // Check if the sticky component is actually sticking
    const affixElement = this.affixRef.current;
    if (affixElement) {
      // For a sticky element with position: sticky; top: 0; margin-top: -101px;
      // The element becomes sticky when its top edge reaches the viewport top (0px)
      // We can detect this by checking if the element's getBoundingClientRect().top equals the CSS top value
      // Use a small threshold to prevent flickering on mobile due to sub-pixel rendering and scroll bounce
      const affixRect = affixElement.getBoundingClientRect();
      const threshold = 2; // 2px threshold to account for scroll bounce and sub-pixel rendering
      const isCurrentlySticky = affixRect.top <= threshold;

      if (isCurrentlySticky !== isSticky) {
        this.setState({ isSticky: isCurrentlySticky });
      }
    }

    const mostVisibleId = maxBy(map(this.navItems, 'id'), getElementVisibleHeight);

    if (mostVisibleId !== selectedSectionId && !isScrolling && !isItemClick) {
      this.selectItem(mostVisibleId);
    }

    if (!isScrolling) {
      this.setState({ isItemClick: false });
    }
  }

  get navItems() {
    const { sections } = this.props;
    return createNavIds(sections);
  }

  navMouseUp() {
    this.setState({
      isMouseDown: false,
    });
  }

  navMouseDown(e) {
    this.setState({
      isMouseDown: true,
      startX: e.pageX - this.itemsWrapperRef.current.offsetLeft,
      scrollLeft: this.itemsWrapperRef.current.scrollLeft,
    });
  }

  selectItem(id) {
    // Always set the selectedSectionId, even if the item is in the MoreSelect dropdown
    this.setState({
      selectedSectionId: id,
    });

    // Only try to scroll/center if the item has a DOM element (i.e., it's a visible Pill)
    if (isNil(this.itemsWrapperRef) || isNil(this.itemRefs[id])) {
      return;
    }

    const wrapper = this.itemsWrapperRef.current;
    const item = this.itemRefs[id].current;

    if (!wrapper || !item) {
      return;
    }

    // Only scroll/center items that are visible as Pills
    centerElementInScrollContainer(wrapper, item);
  }

  scrollToSection(sectionId) {
    const section = find(this.navItems, { id: sectionId });
    if (!section) return;

    scroller.scrollTo(sectionId, {
      duration: 800,
      offset: -(this.inPageNavRef.current.clientHeight + 40),
      smooth: true,
    });

    // make category look like this: 'Popular Menus' instead of `popular-menus`
    if (!isEmpty(section.label)) {
      analytics.track(ANALYTICS_EVENTS.MENU_CATEGORY_NAVIGATION_CLICKED, {
        category: section.label,
      });
    }

    this.setState({ isItemClick: true });
    this.selectItem(sectionId);
  }

  performInitialCategoryScroll() {
    const { router } = this.props;
    const initialSection = router?.asPath.substring(lastIndexOf(router?.asPath, '#'));
    const sectionIds = map(this.navItems, 'id');
    if (includes(sectionIds, initialSection)) {
      this.scrollToSection(initialSection);
    }
  }

  render() {
    const { selectedSectionId, isSticky } = this.state;
    const {
      cartOnClick,
      quantity,
      showDietaryPreferences,
      sections,
      showGroupInfoButton,
      groupInfoOnClick,
      navItemsCount,
      onBannerClick,
      bannerDisabled,
      setShowSearchBar,
      hasOrderSettingsBanner,
    } = this.props;

    return (
      <>
        <Affix hasOrderSettingsBanner={hasOrderSettingsBanner} ref={this.affixRef} isSticky={isSticky}>
          <Box display={hasOrderSettingsBanner ? { xs: 'block', md: 'none' } : 'none'} zIndex="100" width="100%">
            <OrderSettingsSummary
              disabled={bannerDisabled}
              isSticky={isSticky}
              onClick={() => {
                analytics.track(ANALYTICS_EVENTS.ORDER_SETTINGS_OPENED, {
                  location: 'Banner',
                });
                onBannerClick();
              }}
            />
          </Box>
          <Box
            borderRadius={!isSticky && { xs: '20px 20px 0px 0px', md: '32px 32px 0 0' }}
            backgroundColor="warmWhite2"
            mt={hasOrderSettingsBanner && { xs: '-31px', md: 0 }}
            ref={this.inPageNavRef}
          >
            <Box>
              <NavWrapper
                py={{ xs: '18px', md: '26px', lg: '24px' }}
                gap={{ xs: '14px', lg: '24px' }}
                px={{ xs: '20px', md: '24px' }}
                alignItems="center"
                justifyContent="space-between"
              >
                <Flex gap={{ xs: '14px', lg: '24px' }} alignItems="center">
                  {showDietaryPreferences && <DietaryFilter />}

                  <Flex
                    as="button"
                    onClick={() => {
                      setShowSearchBar();
                      setTimeout(() => {
                        const searchBar = document.getElementById('menu-search-bar');
                        if (searchBar) {
                          searchBar.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 100);
                    }}
                  >
                    <Icon name="search" fill="currentColor" height="32px" width="32px" />
                  </Flex>
                  <Nav
                    handleItemClick={this.scrollToSection}
                    handlePageScroll={this.handlePageScroll}
                    itemRefs={this.itemRefs}
                    itemsWrapperRef={this.itemsWrapperRef}
                    navItems={createNavIds(sections)}
                    navItemsCount={navItemsCount}
                    selectedSectionId={selectedSectionId}
                  />
                </Flex>

                <Flex gap="16px">
                  {showGroupInfoButton && (
                    <Box
                      display={{ xs: 'none', lg: 'flex' }}
                      alignItems="center"
                      justifyContent="center"
                      position="relative"
                    >
                      <GroupDetailsButton onClick={groupInfoOnClick} />
                    </Box>
                  )}

                  {cartOnClick && (
                    <Box display={{ xs: 'none', lg: 'flex' }} alignItems="center" position="relative">
                      <CartButton onClick={cartOnClick} quantity={quantity} />
                      <AddedToOrderToast />
                    </Box>
                  )}
                </Flex>
              </NavWrapper>
              <Box borderBottom="1px solid" borderColor="warmWhite3" mx={{ xs: '20px', md: '24px' }} />
            </Box>
          </Box>
        </Affix>
      </>
    );
  }
}

const Affix = styled.div`
  position: sticky;
  top: 0;
  background: transparent;
  z-index: 20;

  ${({ hasOrderSettingsBanner }) =>
    hasOrderSettingsBanner &&
    css`
      margin-top: -101px;

      @media (min-width: ${breakpoints.md}) {
        margin-top: -50px;
      }

      @media (min-width: ${breakpoints.lg}) {
        overflow: visible;
      }
    `};
`;

const NavWrapper = styled(Flex)`
  scrollbar-width: none;
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: ${breakpoints.lg}) {
    overflow: visible;
  }
`;

InPageNav.propTypes = {
  cartOnClick: PropTypes.func,
  quantity: PropTypes.number,
  router: PropTypes.shape({
    asPath: PropTypes.string,
  }).isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }).isRequired
  ).isRequired,
  showDietaryPreferences: PropTypes.bool,
  showGroupInfoButton: PropTypes.bool,
  groupInfoOnClick: PropTypes.func,
  navItemsCount: PropTypes.number.isRequired,
  onBannerClick: PropTypes.func,
  bannerDisabled: PropTypes.bool,
  setShowSearchBar: PropTypes.func,
  hasOrderSettingsBanner: PropTypes.bool,
};

InPageNav.defaultProps = {
  cartOnClick: null,
  quantity: 0,
  showDietaryPreferences: false,
  showGroupInfoButton: false,
  groupInfoOnClick: () => {},
  onBannerClick: () => {},
  bannerDisabled: false,
  setShowSearchBar: () => {},
  hasOrderSettingsBanner: true,
};

const InPageNavWithRouter = observer(withRouter(InPageNav));

// Wrapper component that uses the useIsMinWidth hook
const InPageNavWithMinWidth = React.forwardRef((props, ref) => {
  return <InPageNavWithRouter {...props} ref={ref} />;
});

InPageNavWithMinWidth.displayName = 'InPageNavWithMinWidth';

export { InPageNavWithMinWidth as InPageNav };
